/**
 * ONAIR 재생의 **치명적 오류를 되살릴 것인가** (2026-09-06 무대 신고).
 *
 * ── 왜 필요한가 ─────────────────────────────────────────────
 *
 * v28 이 만든 ONAIR 플레이어는 **`data.fatal` 이면 곧바로 오류 화면**이었다. 그런데
 * 리시버 어댑터(`hls-player.ts`)는 처음부터 `startLoad()`·`recoverMediaError()` 로
 * 되살려 왔다 — **같은 hls.js 를 쓰면서 한쪽만 포기하고 있었다.**
 *
 * 2026-09-06 05:08:44 에 배포로 서버 셋이 재기동했고, 그 몇 초 동안 `/live.m3u8` 이
 * 닿지 않는다. 리시버 채널은 그것을 넘겼고 **ONAIR 채널만 죽었다.**
 *
 * ── 라이브는 더 잘 되살아난다 ───────────────────────────────
 *
 * 플레이리스트가 몇 초마다 갱신되므로 **다시 받으면 그만이다.** 그래서 예산이 리시버
 * (2회, 즉시)보다 크고 **사이에 간격을 둔다** — 재기동은 10초쯤 걸리고, 즉시 두 번
 * 던지는 것은 1초 만에 예산을 태우는 일이다.
 *
 * ★ **상한은 있어야 한다.** 무한 재시도는 오류 화면이 아니라 「조용히 안 나온다」가 되고,
 * 그쪽이 더 나쁘다(리시버 쪽 상수의 그 주석과 같은 판단).
 *
 * ── 왜 SDK 와 공유하지 않는가 ───────────────────────────────
 *
 * 저쪽 판정은 **어댑터 안**에 있고 내보내지 않는다(`packages/bloom-receiver-sdk` 의
 * `arm()` 안이다). 그리고 **예산이 일부러 다르다** — VOD 는 되감아 다시 틀 수 있지만
 * 라이브는 놓치면 그만이라 조금 더 버틴다. 공유하면 그 차이가 사라진다.
 */

/** 즉시 두 번이 아니라 **간격을 두고 다섯 번** — 재기동 10초를 넘긴다 */
export const ONAIR_NET_RETRIES = 5;
export const ONAIR_MEDIA_RETRIES = 2;
export const ONAIR_RETRY_DELAY_MS = 2000;

/**
 * v38 · **뒤처진 시청자가 라이브로 따라붙는 문턱**(조각 수 · 2초 조각이면 22초). hls.js 의 `liveMaxLatencyDurationCount` 이고 기본은
 * 무한이다 — 그러면 한 번 뒤처진 창(재생이 섰다 · 가려진 동안 멈췄다)은 **끝까지 늦다.** CS10 실기에서 두 창이 10초 넘게 갈렸고 새로
 * 고침 뒤에는 0.6초였다 — ONAIR 의 「같은 시각에 켜면 같은 지점」이 시청자 눈에 깨지는 모양이다.
 *
 * 재생목록이 갱신될 때 라이브 끝에서 이보다 뒤에 있으면 시작 지점(끝에서 조각 셋 · `liveSyncDurationCount` 기본 · 위 시작점의 근거와 같다)
 * 으로 건너뛴다. **값이 실측에서 나왔다**(CS8 · hls.js `latency`): 정상 창 9.5~14.4초 · 7번 CSPM 의 스킵 직후 잠깐 19.1초 — 그보다 커야
 * 스킵한 광고 위에서 잘못 건너뛰지 않고, 정상보다 10초쯤 뒤처진 창은 잡는다. 저지연 모드가 아니라 재생 속도로 따라잡는 길
 * (`maxLiveSyncPlaybackRate`)은 hls.js 가 쓰지 않는다 — 건너뛰기 하나다.
 */
export const ONAIR_MAX_LATENCY_SEGMENTS = 11;

/**
 * ONAIR 플레이어의 hls.js 설정 — **한 자리에서만 만든다.**
 *
 * ★ `liveSyncDurationCount` 를 **기본값(3) 그대로 명시한다** — 값을 바꾸는 것이 아니다(시작 지점의 근거 · `use-onair-player.ts` 의 주석).
 * hls.js 는 `liveMaxLatencyDurationCount` 를 주면서 이것을 비워 두면 **생성자에서 던진다**(「must be greater than "liveSyncDurationCount"」 ·
 * `config.ts`) — 처음 판이 그랬고 ONAIR 화면이 통째로 죽었다(2026-10-22 · 타입도 단위 시험도 못 잡았다 · 하네스가 잡았다).
 * 그래서 이 객체로 실제 hls.js 를 만들어 보는 시험이 있다(`hls-recovery.test.ts`).
 */
export const ONAIR_HLS_CONFIG = {
  enableWorker: true,
  liveSyncDurationCount: 3,
  liveMaxLatencyDurationCount: ONAIR_MAX_LATENCY_SEGMENTS,
} as const;

export type OnairRecovery = 'reload' | 'recoverMedia' | 'fail';

/**
 * @param type hls.js 의 `ErrorTypes` 값. **문자열로 받는다** — 이 함수가 hls.js 를
 *   import 하면 순수 모듈이 아니게 되고, 그 값은 `'networkError'`·`'mediaError'` 다
 */
export function recoverFatal(input: {
  type: string;
  netTried: number;
  mediaTried: number;
}): OnairRecovery {
  if (input.type === 'networkError' && input.netTried < ONAIR_NET_RETRIES) return 'reload';
  if (input.type === 'mediaError' && input.mediaTried < ONAIR_MEDIA_RETRIES) return 'recoverMedia';
  return 'fail';
}
